
import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { UploadCloud, X, Loader2, Image as ImageIcon, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { SupabaseStorageService } from '@/services/photo/supabaseStorageService';

interface PhotoUploadSectionProps {
  onPhotoUploaded: (url: string) => void;
  initialPhotoUrl?: string;
  compact?: boolean;
}

export const PhotoUploadSection: React.FC<PhotoUploadSectionProps> = ({
  onPhotoUploaded,
  initialPhotoUrl,
  compact = false
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(initialPhotoUrl || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setIsUploading(true);
      setUploadError(null);
      
      // Validate file
      if (!SupabaseStorageService.isValidImageFile(file)) {
        setUploadError('Ugyldig filformat. Bruk JPEG, PNG, GIF eller WEBP.');
        toast.error('Ugyldig filformat. Bruk JPEG, PNG, GIF eller WEBP.');
        setIsUploading(false);
        return;
      }
      
      if (!SupabaseStorageService.isValidFileSize(file, 5)) {
        setUploadError('Filen er for stor. Maksimal størrelse er 5MB.');
        toast.error('Filen er for stor. Maksimal størrelse er 5MB.');
        setIsUploading(false);
        return;
      }
      
      // Upload to Supabase Storage directly (no blob preview)
      console.log('[PhotoUploadSection] Starting upload to Supabase Storage...');
      const uploadedUrl = await SupabaseStorageService.uploadImage(file, 'interview-photos');
      console.log('[PhotoUploadSection] Upload successful:', uploadedUrl);
      
      // Update preview with the actual URL
      setPreviewUrl(uploadedUrl);
      
      // Pass the URL to the parent component
      onPhotoUploaded(uploadedUrl);
      
      toast.success('Profilbilde lastet opp til Supabase Storage');
    } catch (error) {
      console.error('[PhotoUploadSection] Upload error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Ukjent feil';
      setUploadError(`Feil ved opplasting: ${errorMessage}`);
      toast.error('Kunne ikke laste opp bildet. Vennligst prøv igjen.');
    } finally {
      setIsUploading(false);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const handleRemoveImage = () => {
    if (previewUrl) {
      setPreviewUrl(null);
      onPhotoUploaded(''); // Clear the photo URL
      toast.success('Profilbilde fjernet');
    }
  };
  
  // Compact mode for the editor dialog
  if (compact) {
    return (
      <div className="w-full">
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept="image/*"
          onChange={handleFileChange}
        />
        
        <div className="flex items-center gap-3">
          {previewUrl ? (
            <div className="relative">
              <img
                src={`${previewUrl}?t=${Date.now()}`}
                alt="Profilbilde"
                className="w-16 h-16 rounded-full object-cover border border-gray-200"
                onError={() => {
                  console.error('[PhotoUploadSection] Error loading image from URL:', previewUrl);
                  setUploadError('Kunne ikke laste bildet. Prøv igjen.');
                }}
              />
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="h-5 w-5 rounded-full absolute -top-1 -right-1"
                onClick={handleRemoveImage}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <div 
              className="w-16 h-16 border-2 border-dashed border-gray-300 rounded-full flex items-center justify-center hover:border-gray-400 transition-colors cursor-pointer bg-gray-50"
              onClick={() => fileInputRef.current?.click()}
            >
              <Camera className="h-6 w-6 text-gray-400" />
            </div>
          )}
          
          <div className="flex flex-col">
            <Button 
              type="button" 
              variant="outline" 
              size="sm" 
              className="mb-1"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              {isUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Laster opp...
                </>
              ) : (
                <>
                  <UploadCloud className="mr-2 h-4 w-4" />
                  {previewUrl ? 'Bytt bilde' : 'Last opp bilde'}
                </>
              )}
            </Button>
            {previewUrl && (
              <Button 
                type="button" 
                variant="ghost" 
                size="sm"
                onClick={handleRemoveImage}
                disabled={isUploading}
              >
                <X className="mr-1 h-4 w-4" />
                Fjern bilde
              </Button>
            )}
          </div>
        </div>
        
        {uploadError && (
          <p className="mt-2 text-sm text-red-500">
            {uploadError}
          </p>
        )}
      </div>
    );
  }
  
  // Original full-size mode
  return (
    <div className="w-full max-w-sm">
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/*"
        onChange={handleFileChange}
      />
      
      {previewUrl ? (
        <div className="relative rounded-md overflow-hidden border border-gray-200">
          <img
            src={`${previewUrl}?t=${Date.now()}`}
            alt="Profilbilde"
            className="w-full h-auto max-h-48 object-cover"
            onError={() => {
              console.error('[PhotoUploadSection] Error loading image from URL:', previewUrl);
              setUploadError('Kunne ikke laste bildet. Prøv igjen.');
            }}
          />
          <div className="absolute top-0 right-0 p-1">
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="h-7 w-7 rounded-full"
              onClick={handleRemoveImage}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="p-2 bg-white border-t border-gray-200">
            <Button 
              type="button" 
              variant="outline" 
              size="sm" 
              className="w-full"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              {isUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Laster opp...
                </>
              ) : (
                <>
                  <UploadCloud className="mr-2 h-4 w-4" />
                  Endre bilde
                </>
              )}
            </Button>
          </div>
        </div>
      ) : (
        <div 
          className="border-2 border-dashed border-gray-300 rounded-md p-6 flex flex-col items-center justify-center hover:border-gray-400 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <ImageIcon className="h-10 w-10 text-gray-400 mb-3" />
          <p className="text-sm text-gray-500 text-center mb-1">
            Klikk for å laste opp profilbilde
          </p>
          <p className="text-xs text-gray-400">
            Støtter JPEG, PNG, GIF, WEBP (maks 5MB)
          </p>
          {isUploading && (
            <div className="mt-3 flex items-center">
              <Loader2 className="h-4 w-4 text-blue-500 animate-spin mr-2" />
              <span className="text-sm text-blue-500">Laster opp...</span>
            </div>
          )}
        </div>
      )}
      
      {uploadError && (
        <p className="mt-2 text-sm text-red-500">
          {uploadError}
        </p>
      )}
    </div>
  );
};
